import { createClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

// Add type declaration for window.__env
declare global {
  interface Window {
    __env?: {
      VITE_SUPABASE_URL?: string;
      VITE_SUPABASE_ANON_KEY?: string;
      VITE_SUPABASE_SERVICE_ROLE_KEY?: string;
      NEXT_PUBLIC_SUPABASE_URL?: string;
      NEXT_PUBLIC_SUPABASE_ANON_KEY?: string;
      [key: string]: string | undefined;
    };
  }
}

// Utility function to validate URL
function isValidUrl(url: string | undefined): boolean {
  if (!url || url.trim() === '') return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Try to get environment variables from various sources
// This helps debug issues with different environment configurations
const supabaseUrl = 
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) || 
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_SUPABASE_URL) || 
  (typeof window !== 'undefined' && window.__env && (window.__env.VITE_SUPABASE_URL || window.__env.NEXT_PUBLIC_SUPABASE_URL)) ||
  process.env?.VITE_SUPABASE_URL;
                   
const supabaseServiceRoleKey = 
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_SERVICE_ROLE_KEY) ||
  (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_SERVICE_ROLE_KEY) || 
  (typeof process !== 'undefined' && process.env?.SUPABASE_SERVICE_ROLE_KEY) || 
  (typeof window !== 'undefined' && window.__env && window.__env.VITE_SUPABASE_SERVICE_ROLE_KEY) ||
  process.env?.VITE_SUPABASE_SERVICE_ROLE_KEY ||
  process.env?.SUPABASE_SERVICE_ROLE_KEY;

// Check if we're in development mode
const isDev = 
  (typeof import.meta !== 'undefined' && import.meta.env?.DEV) ||
  process.env.NODE_ENV === 'development';

// Check if we're in test mode
const isTest = 
  process.env.NODE_ENV === 'test' ||
  (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test');

// Validate the URL before proceeding
const hasValidUrl = isValidUrl(supabaseUrl);
const hasValidServiceRoleKey = !!(supabaseServiceRoleKey && supabaseServiceRoleKey.trim() !== '');

// Log useful diagnostic information
console.log('Supabase Admin Client - URL available:', hasValidUrl);
console.log('Supabase Admin Client - Service Role Key available:', hasValidServiceRoleKey);

if (!hasValidUrl || !hasValidServiceRoleKey) {
  console.error('Missing or invalid Supabase admin credentials:', {
    urlProvided: !!supabaseUrl,
    urlValid: hasValidUrl,
    serviceRoleKeyProvided: !!supabaseServiceRoleKey,
    serviceRoleKeyValid: hasValidServiceRoleKey,
    url: supabaseUrl ? `${supabaseUrl.substring(0, 30)}...` : 'none'
  });
  
  // In development (but not test), throw immediately. In production and test, try to continue with limited functionality.
  if (isDev && !isTest) {
    throw new Error('Missing or invalid Supabase admin credentials');
  } else {
    console.warn('Admin features will be limited or unavailable due to missing credentials');
  }
}

// Check if we already have an admin client instance in the global space
const globalWithAdminClient = globalThis as unknown as { supabaseAdminClient?: ReturnType<typeof createClient<Database>> };

// Only create the client if we have valid credentials
let supabaseAdmin: ReturnType<typeof createClient<Database>> | null = null;

// Note: This client will produce a console warning about "Multiple GoTrueClient instances"
// This is expected and can be safely ignored since we use different storage keys
// for the regular client ('supabase-client-auth') and admin client ('supabase-admin-auth')
if (hasValidUrl && hasValidServiceRoleKey) {
  try {
    supabaseAdmin = globalWithAdminClient.supabaseAdminClient ?? createClient<Database>(
      supabaseUrl!,
      supabaseServiceRoleKey!,
      {
        auth: {
          autoRefreshToken: false,
          persistSession: false,
          detectSessionInUrl: false,
          // Use a unique storage key to prevent conflicts with the regular client
          storageKey: 'supabase-admin-auth'
        },
        global: {
          headers: {
            'X-Client-Info': 'supabase-js-admin'
          }
        }
      }
    );

    // Store the admin client instance globally to prevent multiple instances
    if (!globalWithAdminClient.supabaseAdminClient) {
      globalWithAdminClient.supabaseAdminClient = supabaseAdmin;
    }
  } catch (error) {
    console.error('Failed to create Supabase admin client:', error);
    supabaseAdmin = null;
  }
}

// Export the client, which might be null in production if credentials are missing
export { supabaseAdmin }; 